/**
 * EHS Management Information Report.
 *
 * The comprehensive management MIS: seventeen sections of KPIs drawn from the
 * shared metric registry, with the detail tables a count cannot express (risk
 * matrix, injuries by body part, the open-action tracker).
 *
 * Distinct from /safety/ehs-mis, which reproduces the client's 73-row workbook
 * grid. This page is the management pack.
 *
 * Two things the UI must not blur, because a manager acts on them differently:
 *   - "N/A" is no data captured; "0" is a real zero. Never rendered as the same.
 *   - a section with no source data says so, with the reason, rather than
 *     appearing as an empty panel that reads as "nothing to report".
 *
 * Scope and authorization are the server's: it resolves the site fail-closed and
 * authorizes each metric against its own module grant, so a section the user may
 * not see comes back marked restricted.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Download, FileDown,
  FileSpreadsheet, FileText, Info, Loader2, RefreshCw, Lock, Minus,
  CheckCircle2, MonitorPlay, Presentation, TrendingDown, TrendingUp, Table2,
} from 'lucide-react';
// The shared recharts wrapper — no page hand-rolls charts (see @/dashboard).
import { ChartWidget } from '@/dashboard';
import TabNavigation from '../../components/TabNavigation';
import MisViewSwitcher from './MisViewSwitcher';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  fetchMisSections, downloadMisReport,
} from '../../services/ehsMisManagementApi';
import { saveBlob } from '../../services/ehsMisApi';

const TIME_PRESETS = [
  { id: 'current_month', label: 'Current month' },
  { id: 'previous_month', label: 'Previous month' },
  { id: 'quarter_to_date', label: 'Quarter to date' },
  { id: 'half_yearly', label: 'Half year' },
  { id: 'financial_year', label: 'Financial year' },
  { id: 'yearly', label: 'Calendar year' },
  { id: 'last_30_days', label: 'Last 30 days' },
];

const DOWNLOADS = [
  // Two forms of the same executive deck — scorecard, ranked findings, one theme
  // per slide. The PDF is the finished document, for issuing and archiving; the
  // PowerPoint is the editable one, for a board pack somebody will re-order.
  { id: 'presentation', label: 'Presentation deck (PDF)', Icon: Presentation },
  { id: 'pptx', label: 'PowerPoint deck (editable)', Icon: MonitorPlay },
  { id: 'pdf', label: 'Detailed PDF', Icon: FileText },
  { id: 'excel', label: 'Excel workbook', Icon: FileSpreadsheet },
  { id: 'csv', label: 'CSV', Icon: FileDown },
];

// Verdict tone -> banner styling. The tone is the server's, computed from the
// worst finding rather than an average, so a fatality never reads as green.
const VERDICT_TONE = {
  critical: { cls: 'bg-rose-50 border-rose-300 text-rose-900', Icon: AlertTriangle,
              label: 'Immediate attention' },
  warning: { cls: 'bg-amber-50 border-amber-300 text-amber-900', Icon: AlertTriangle,
             label: 'Off target' },
  watch: { cls: 'bg-slate-50 border-slate-300 text-slate-800', Icon: Info,
           label: 'Watch' },
  clear: { cls: 'bg-emerald-50 border-emerald-300 text-emerald-900', Icon: CheckCircle2,
           label: 'On track' },
  unknown: { cls: 'bg-slate-50 border-slate-300 text-slate-700', Icon: Info,
             label: 'No data' },
};

const SEVERITY_CHIP = {
  critical: 'bg-rose-600 text-white',
  warning: 'bg-amber-500 text-white',
  watch: 'bg-slate-400 text-white',
};
const SEVERITY_WORD = { critical: 'Critical', warning: 'Off target', watch: 'Watch' };

// Availability drives how a section presents itself. `partial` deliberately has
// no badge: most sections are partial, so badging them all would be noise —
// their limitation text carries the detail instead.
const AVAILABILITY_BADGE = {
  unavailable: { label: 'No source data', cls: 'bg-amber-100 text-amber-800' },
  // "we don't do this" reads completely differently from "we couldn't source
  // this", so it gets its own neutral badge rather than the amber warning.
  not_applicable: { label: 'Not applicable', cls: 'bg-gray-100 text-gray-600' },
  restricted: { label: 'Restricted', cls: 'bg-slate-200 text-slate-700' },
};

// RAG comes from the server, which only sets it when a target exists AND the
// metric declares a direction — so a colour here always has a basis.
const RAG = {
  green: { cls: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-800', label: 'On target' },
  amber: { cls: 'text-amber-700', chip: 'bg-amber-100 text-amber-800', label: 'Watch' },
  red: { cls: 'text-rose-700', chip: 'bg-rose-100 text-rose-800', label: 'Off target' },
};

const numberFmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

/** A KPI value as it should read. null is N/A — never 0. */
const formatValue = (kpi) => {
  if (kpi.value === null || kpi.value === undefined) return 'N/A';
  const n = Number(kpi.value);
  if (!Number.isFinite(n)) return String(kpi.value);
  if (kpi.format === 'percent') return `${numberFmt.format(n)}%`;
  if (kpi.format === 'rate') return n.toFixed(2);
  return numberFmt.format(n);
};

/**
 * Colour a KPI only when the metric declares a direction AND has a value.
 * Without a target there is no pass/fail, so this shades by direction only —
 * it is a hint, not a RAG status, and is deliberately understated.
 */
const valueTone = (kpi) => {
  if (kpi.value === null || kpi.value === undefined) return 'text-gray-400';
  // A target gives a real verdict; prefer it over the directional hint.
  if (kpi.status && RAG[kpi.status]) return RAG[kpi.status].cls;
  if (kpi.higher_is_better === false && Number(kpi.value) > 0) return 'text-rose-700';
  if (kpi.higher_is_better === true && Number(kpi.value) === 0) return 'text-amber-700';
  return 'text-gray-900';
};

/** Signed variance against target, with percentage when meaningful. */
const formatVariance = (kpi) => {
  if (kpi.variance === null || kpi.variance === undefined) return null;
  const sign = kpi.variance > 0 ? '+' : '';
  const base = `${sign}${numberFmt.format(kpi.variance)}`;
  if (kpi.variance_pct === null || kpi.variance_pct === undefined) return base;
  return `${base} (${sign}${numberFmt.format(kpi.variance_pct)}%)`;
};

/**
 * An info toggle that reveals a KPI's definition.
 *
 * Same treatment as the dashboard tiles (@/dashboard KpiWidget): the registry has
 * always carried a definition, and an acronym card — TRIR, LTIFR, LWC Rate — says
 * nothing to a reader who does not already know the term. Shown on demand rather
 * than spending permanent card space on it, because a manager scanning eighteen
 * sections wants the numbers, and the reader who needs the definition needs it
 * once.
 */
const InfoToggle = ({ label, open, onToggle }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    aria-expanded={open}
    aria-label={`What is ${label}?`}
    title={`What is ${label}?`}
    className={`shrink-0 rounded p-0.5 transition-colors focus:outline-none
                focus:ring-2 focus:ring-blue-400/60
                ${open ? 'text-blue-600' : 'text-gray-300 hover:text-gray-500'}`}
  >
    <Info className="h-3.5 w-3.5" />
  </button>
);

const KpiDefinition = ({ children }) => (
  <div className="mt-2 rounded-md bg-gray-50 px-2.5 py-2 text-[11px] leading-relaxed
                  text-gray-600">
    {children}
  </div>
);

const KpiCard = ({ kpi }) => {
  const rag = kpi.status ? RAG[kpi.status] : null;
  const variance = formatVariance(kpi);
  const hasTarget = kpi.target !== null && kpi.target !== undefined;
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-gray-500 leading-tight">{kpi.label}</div>
        <div className="flex items-center gap-1">
          {rag && (
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${rag.chip}`}>
              {rag.label}
            </span>
          )}
          {kpi.description && (
            <InfoToggle label={kpi.label} open={showInfo}
                        onToggle={() => setShowInfo((v) => !v)} />
          )}
        </div>
      </div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${valueTone(kpi)}`}>
        {formatValue(kpi)}
      </div>
      {hasTarget ? (
        // With a target the subtitle carries the judgement: "vs target" is what
        // a manager acts on, and the unit is secondary.
        <div className="text-[10px] text-gray-500 mt-0.5">
          Target {numberFmt.format(kpi.target)}
          {variance && <span className="text-gray-400"> &middot; {variance}</span>}
        </div>
      ) : (
        <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-0.5">
          {kpi.unit || kpi.basis || ' '}
        </div>
      )}
      {showInfo && kpi.description && (
        <KpiDefinition>{kpi.description}</KpiDefinition>
      )}
    </div>
  );
};

/**
 * A hero KPI. Bigger than a section card and carries its own context — target,
 * movement, basis — because these six are the figures a reader acts on before
 * scrolling anywhere.
 */
const HeroKpi = ({ kpi }) => {
  const rag = kpi.status ? RAG[kpi.status] : null;
  const change = typeof kpi.trend === 'number' ? kpi.trend : null;
  const [showInfo, setShowInfo] = useState(false);
  // Whether a move is good depends on the metric, not on its sign: fewer
  // incidents is good, fewer toolbox talks is not.
  const goodMove = change === null || kpi.higher_is_better === null
    || kpi.higher_is_better === undefined
    ? null : (change > 0) === Boolean(kpi.higher_is_better);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium text-gray-500 leading-tight">{kpi.label}</div>
        <div className="flex items-center gap-1">
          {rag && (
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold whitespace-nowrap ${rag.chip}`}>
              {rag.label}
            </span>
          )}
          {kpi.description && (
            <InfoToggle label={kpi.label} open={showInfo}
                        onToggle={() => setShowInfo((v) => !v)} />
          )}
        </div>
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular-nums ${valueTone(kpi)}`}>
        {formatValue(kpi)}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        {kpi.target !== null && kpi.target !== undefined && (
          <span className="text-gray-500">Target {numberFmt.format(kpi.target)}</span>
        )}
        {change !== null && change !== 0 && (
          <span className={goodMove === null ? 'text-gray-500'
            : goodMove ? 'text-emerald-700' : 'text-rose-700'}>
            {change > 0 ? <TrendingUp className="h-3 w-3 inline mr-0.5" />
              : <TrendingDown className="h-3 w-3 inline mr-0.5" />}
            {Math.abs(change)}% vs previous
          </span>
        )}
        {kpi.basis && <span className="text-gray-400">{kpi.basis}</span>}
      </div>
      {showInfo && kpi.description && (
        <KpiDefinition>{kpi.description}</KpiDefinition>
      )}
    </div>
  );
};

/** Ranked findings. Clicking one opens the section it came from. */
const AttentionPanel = ({ executive, onJump }) => {
  const findings = executive.attention || [];
  const total = executive.attention_total || findings.length;
  if (!findings.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600" />
        <h2 className="font-semibold text-gray-900">Needs attention</h2>
        <span className="text-xs text-gray-500">
          worst first{total > findings.length ? ` · showing ${findings.length} of ${total}` : ''}
        </span>
      </div>
      <ul className="divide-y divide-gray-100">
        {findings.map((f, i) => (
          <li key={`${f.metric_key}-${i}`}>
            <button onClick={() => onJump(f.section_key)}
                    className="w-full text-left px-5 py-2.5 hover:bg-gray-50 flex items-start gap-3">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold mt-0.5
                                whitespace-nowrap ${SEVERITY_CHIP[f.severity] || SEVERITY_CHIP.watch}`}>
                {SEVERITY_WORD[f.severity] || f.severity}
              </span>
              <span className="flex-1">
                <span className="text-sm text-gray-900">{f.title}</span>
                <span className="block text-xs text-gray-500">
                  {f.detail}
                  {f.section_title && <span className="text-gray-400"> · {f.section_title}</span>}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 text-gray-300 mt-1 flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** What moved, and in which direction it counts. */
const MovementPanel = ({ executive }) => {
  const improving = executive.improving || [];
  const deteriorating = executive.deteriorating || [];
  if (!improving.length && !deteriorating.length) return null;
  const column = (items, tone, Icon, heading) => (
    <div>
      <div className={`flex items-center gap-1.5 text-xs font-semibold mb-2 ${tone}`}>
        <Icon className="h-3.5 w-3.5" />
        {heading}
      </div>
      <ul className="space-y-1">
        {items.length === 0 && <li className="text-xs text-gray-400">Nothing material</li>}
        {items.map((m) => (
          <li key={m.metric_key} className="text-xs text-gray-600 leading-snug">{m.text}</li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
      <div className="text-sm font-semibold text-gray-900 mb-3">
        Movement against the previous period
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {column(deteriorating, 'text-rose-700', TrendingDown, 'Worsened')}
        {column(improving, 'text-emerald-700', TrendingUp, 'Improved')}
      </div>
      <p className="text-[11px] text-gray-400 mt-3">
        Changes of 10% or more. Point-in-time backlogs are excluded — they have no
        previous snapshot to compare against.
      </p>
    </div>
  );
};

/**
 * On-screen charts, built from the SAME metric keys the server-side deck uses
 * (services/ehs_mis_deck._theme_chart), so the screen and the downloaded deck
 * show the same picture. A chart whose series is entirely zero is dropped rather
 * than drawn as an empty frame.
 *
 * Every value is printed on the chart. A management report gets projected in a
 * review meeting and printed into a pack, and a figure that only exists in a
 * hover tooltip is not in the room when the question is asked.
 */
const CHART_SPECS = [
  {
    key: 'incident-mix', title: 'Incident severity mix', type: 'donut', height: 240,
    build: (v) => [
      { name: 'Fatality', value: v('safety.incidents.fatality') },
      { name: 'Lost time', value: v('safety.incidents.lti') },
      { name: 'Medical treatment', value: v('safety.incidents.mtc') },
      { name: 'First aid', value: v('safety.incidents.first_aid') },
      { name: 'Near miss', value: v('safety.near_miss.total') },
    ],
  },
  {
    key: 'rates', title: 'Frequency rates', type: 'hbar', height: 240,
    build: (v) => [
      { name: 'LTIFR', value: v('safety.ltifr') },
      { name: 'TRIR', value: v('safety.trir') },
      { name: 'Lost workday rate', value: v('safety.lwc_rate') },
      { name: 'Near-miss rate', value: v('safety.near_miss_rate') },
    ],
  },
  {
    key: 'capa-ageing', title: 'Open corrective actions by age', type: 'bar', height: 240,
    build: (v) => [
      { name: '0-7 days', value: v('capa.aging.0_7') },
      { name: '8-30', value: v('capa.aging.8_30') },
      { name: '31-60', value: v('capa.aging.31_60') },
      { name: '61-90', value: v('capa.aging.61_90') },
      { name: 'over 90', value: v('capa.aging.over_90') },
    ],
  },
  {
    key: 'compliance', title: 'Compliance position', type: 'donut', height: 240,
    build: (v) => [
      { name: 'Met', value: v('compliance.completed') },
      { name: 'Overdue', value: v('compliance.overdue') },
      { name: 'Evidence missing', value: v('compliance.evidence_missing') },
      { name: 'Non-compliance', value: v('compliance.non_compliances') },
    ],
  },
  {
    key: 'observations', title: 'Observation mix', type: 'donut', height: 240,
    build: (v) => [
      { name: 'Unsafe acts', value: v('safety.observations.unsafe_acts') },
      { name: 'Unsafe conditions', value: v('safety.observations.unsafe_conditions') },
      { name: 'Near misses', value: v('safety.near_miss.total') },
    ],
  },
  {
    key: 'engagement', title: 'Engagement by meeting type', type: 'bar', height: 240,
    build: (v) => [
      { name: 'Toolbox', value: v('meetings.toolbox_talks') },
      { name: 'Committee', value: v('meetings.safety_committee') },
      { name: 'Mgmt review', value: v('meetings.management_review') },
      { name: 'Training', value: v('meetings.training_sessions') },
      { name: 'Incident review', value: v('meetings.incident_reviews') },
    ],
  },
  {
    key: 'hazards', title: 'Hazards by risk level', type: 'bar', height: 240,
    build: (v) => [
      { name: 'Critical', value: v('risk.hazards.critical') },
      { name: 'High', value: v('risk.hazards.high') },
      { name: 'Medium', value: v('risk.hazards.medium') },
      { name: 'Low', value: v('risk.hazards.low') },
    ],
  },
];

const ChartGrid = ({ byKey }) => {
  const value = (key) => {
    const v = byKey[key]?.value;
    return typeof v === 'number' ? v : 0;
  };
  const charts = CHART_SPECS
    .map((spec) => ({ spec, data: spec.build(value).filter((d) => d.value > 0) }))
    // One bar is not a distribution, and an empty donut says nothing.
    .filter(({ data }) => data.length >= 2);
  if (!charts.length) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      {charts.map(({ spec, data }) => (
        <ChartWidget
          key={spec.key} type={spec.type} title={spec.title} data={data}
          xKey="name" height={spec.height}
          series={[{ key: 'value', label: spec.title }]}
          // Values on the chart, not only in the tooltip: this page is read on a
          // projector and printed for a month-end review, where nobody can hover.
          showValues
          valueFormat={(v) => numberFmt.format(v)}
        />
      ))}
    </div>
  );
};

const DetailTable = ({ table }) => (
  <div className="mt-4">
    <h4 className="text-sm font-semibold text-gray-800 mb-2">{table.title}</h4>
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {table.columns.map((c, i) => (
              <th key={i}
                  className={`px-3 py-2 font-semibold text-gray-700 whitespace-nowrap ${
                    i === 0 ? 'text-left' : 'text-right'
                  }`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} className="border-t border-gray-100">
              {row.map((cell, ci) => (
                <td key={ci}
                    className={`px-3 py-1.5 whitespace-nowrap ${
                      ci === 0 ? 'text-gray-900' : 'text-right tabular-nums text-gray-700'
                    } ${cell === 'Overdue' ? 'text-rose-700 font-medium' : ''}`}>
                  {cell || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const EHSManagementMIS = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showError } = useAlert();
  const { activeSiteId, availableSites } = useSite();

  const [preset, setPreset] = useState('financial_year');
  const [standard, setStandard] = useState('india');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  // The full KPI tables are the detail behind the executive view, not the view
  // itself: hidden until asked for, which is the whole point of this page.
  const [showData, setShowData] = useState(false);

  const siteId = activeSiteId || '';
  const needsSiteChoice = !siteId && availableSites.length > 1;

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMisSections(
        { siteId: siteId || undefined, preset, standard }, { signal });
      setReport(data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'You do not have permission to view the EHS MIS.'
          : err?.response?.data?.detail || 'Could not load the report.',
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [siteId, preset, standard]);

  useEffect(() => {
    if (needsSiteChoice) { setReport(null); return undefined; }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, needsSiteChoice]);

  // A new report collapses every section again: the reader is meant to land on
  // the executive view, not on eighteen open panels.
  useEffect(() => {
    if (!report) return;
    const next = {};
    for (const section of report.sections || []) next[section.key] = true;
    setCollapsed(next);
  }, [report]);

  const handleDownload = async (format) => {
    setDownloading(format);
    setMenuOpen(false);
    try {
      const { blob, filename } = await downloadMisReport({
        siteId: siteId || undefined, preset, standard, format,
      });
      saveBlob(blob, filename);
    } catch (err) {
      showError(err?.response?.data?.detail || `Could not download the ${format}.`);
    } finally {
      setDownloading(null);
    }
  };

  const sections = report?.sections || [];
  const details = report?.details || {};
  const executive = report?.executive || null;

  // Every KPI by metric key, for the on-screen charts. Built once per report.
  const byKey = useMemo(() => {
    const out = {};
    for (const section of sections) {
      for (const kpi of section.kpis || []) {
        if (kpi.key && !(kpi.key in out)) out[kpi.key] = kpi;
      }
    }
    return out;
  }, [sections]);

  // Opening a finding reveals the data and expands the section it came from.
  const jumpToSection = useCallback((key) => {
    if (!key) return;
    setShowData(true);
    setCollapsed((c) => ({ ...c, [key]: false }));
    // Deferred so the section exists in the DOM before it is scrolled to.
    setTimeout(() => {
      document.getElementById(`mis-section-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, []);

  // Headline count so a reader knows how much of the pack is actually sourced.
  const coverage = useMemo(() => {
    const total = sections.length;
    const withData = sections.filter(
      (s) => (s.kpis || []).some((k) => k.value !== null && k.value !== undefined),
    ).length;
    return { total, withData };
  }, [sections]);

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/safety')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Safety
          </button>
          {/* Both MIS views live behind one Safety Hub card. Nothing is unsaved
              on this view, so the switch needs no guard. */}
          <MisViewSwitcher active="management" />
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                EHS Management Information Report
              </h1>
              <p className="text-gray-600 mt-1">
                {report
                  ? `${report.range?.label || ''} · ${coverage.withData} of `
                    + `${coverage.total} sections carrying data`
                  : 'Management-level EHS performance across every module'}
              </p>
              {report?.template?.name && (
                <p className="text-xs text-gray-500 mt-1">
                  Template: {report.template.name}
                  {!report.template.id && ' (built-in)'}
                </p>
              )}
            </div>
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)}
                      disabled={!report || Boolean(downloading)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white
                                 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {downloading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Download className="h-4 w-4" />}
                Download
              </button>
              {menuOpen && report && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200
                                rounded-lg shadow-lg z-20">
                  {DOWNLOADS.map(({ id, label, Icon }) => (
                    <button key={id} onClick={() => handleDownload(id)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm
                                       text-gray-700 hover:bg-gray-50 text-left">
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="mis-preset" className="block text-xs font-medium text-gray-500 mb-1">
                Period
              </label>
              <select id="mis-preset" value={preset}
                      onChange={(e) => setPreset(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {TIME_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="mis-std" className="block text-xs font-medium text-gray-500 mb-1"
                     title="Normalisation base for frequency rates.">
                Rate basis
              </label>
              <select id="mis-std" value={standard}
                      onChange={(e) => setStandard(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="india">Per 1,000,000 hrs (IS 3786)</option>
                <option value="osha">Per 200,000 hrs (OSHA)</option>
              </select>
            </div>
            <button onClick={() => load()} disabled={loading || needsSiteChoice}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300
                               rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="ml-auto text-xs text-gray-500">
              Site is chosen in the header bar.
            </span>
          </div>
        </div>

        {needsSiteChoice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Pick a site in the header bar to run the report.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* ---- Executive view: verdict, hero KPIs, findings, movement ---- */}
        {executive && (
          <>
            {executive.verdict && (() => {
              const tone = VERDICT_TONE[executive.verdict.tone] || VERDICT_TONE.unknown;
              const { Icon } = tone;
              return (
                <div className={`border rounded-xl p-4 mb-5 flex items-start gap-3 ${tone.cls}`}>
                  <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold">{executive.verdict.text}</div>
                    <div className="text-xs opacity-80 mt-0.5">
                      {executive.coverage?.with_data ?? 0} of{' '}
                      {executive.coverage?.sections ?? 0} sections carried data
                      {executive.coverage?.not_applicable
                        ? ` · ${executive.coverage.not_applicable} not applicable to this site` : ''}
                      {executive.coverage?.restricted
                        ? ` · ${executive.coverage.restricted} restricted for your role` : ''}
                    </div>
                  </div>
                </div>
              );
            })()}

            {(executive.headline || []).length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                {executive.headline.map((kpi) => <HeroKpi key={kpi.key} kpi={kpi} />)}
              </div>
            )}

            <AttentionPanel executive={executive} onJump={jumpToSection} />
            <MovementPanel executive={executive} />
            <ChartGrid byKey={byKey} />

            {/* The eighteen sections of KPI tables sit behind this switch. */}
            <button onClick={() => setShowData((v) => !v)}
                    className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-2.5
                               bg-white border border-gray-300 rounded-lg text-sm text-gray-700
                               hover:bg-gray-50">
              <Table2 className="h-4 w-4" />
              {showData ? 'Hide full data' : `Show full data (${sections.length} sections, `
                + `${sections.reduce((n, sec) => n + (sec.kpis || []).length, 0)} indicators)`}
              {showData ? <ChevronDown className="h-4 w-4" />
                : <ChevronRight className="h-4 w-4" />}
            </button>
          </>
        )}

        {loading && !report && (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Computing indicators…
          </div>
        )}

        {(showData || !executive) && sections.map((section) => {
          const badge = AVAILABILITY_BADGE[section.availability];
          const isCollapsed = collapsed[section.key];
          const kpis = section.kpis || [];
          const sectionDetails = Object.values(details[section.key] || {})
            .filter((t) => (t.rows || []).length && !t.chart_only);

          return (
            <div key={section.key} id={`mis-section-${section.key}`}
                 className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4
                            scroll-mt-24">
              <button onClick={() => toggle(section.key)}
                      className="w-full flex items-start gap-3 p-5 text-left hover:bg-gray-50/70
                                 rounded-xl">
                {isCollapsed
                  ? <ChevronRight className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  : <ChevronDown className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                    {badge && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${badge.cls}`}>
                        {badge.label}
                      </span>
                    )}
                    {section.tenant_wide && (
                      <span title="This source table has no site_id column, so the figures cover the whole tenant."
                            className="px-2 py-0.5 rounded text-[10px] font-medium
                                       bg-sky-100 text-sky-800 cursor-help">
                        tenant-wide
                      </span>
                    )}
                    {kpis.length > 0 && (
                      <span className="text-xs text-gray-400">{kpis.length} indicators</span>
                    )}
                  </div>
                  {section.description && !isCollapsed && (
                    <p className="text-sm text-gray-500 mt-1">{section.description}</p>
                  )}
                </div>
              </button>

              {!isCollapsed && (
                <div className="px-5 pb-5">
                  {section.availability === 'not_applicable' && (
                    <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50
                                    border border-gray-200 rounded-lg p-3">
                      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Marked not applicable to this site, so it is reported but
                        not measured. This is a configuration choice, not missing data.
                      </span>
                    </div>
                  )}

                  {section.availability === 'unavailable' && (
                    <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50
                                    border border-amber-200 rounded-lg p-3">
                      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{section.limitation}</span>
                    </div>
                  )}

                  {section.availability === 'restricted' && (
                    <div className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50
                                    border border-slate-200 rounded-lg p-3">
                      <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Your role does not hold the permission that governs this
                        section's source module.
                      </span>
                    </div>
                  )}

                  {kpis.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {kpis.map((kpi) => <KpiCard key={kpi.key} kpi={kpi} />)}
                    </div>
                  )}

                  {sectionDetails.map((table, i) => (
                    <DetailTable key={i} table={table} />
                  ))}

                  {section.availability === 'partial' && section.limitation && (
                    <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                      <span className="font-medium text-gray-600">Limitations: </span>
                      {section.limitation}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EHSManagementMIS;
